/**
 * Processing Operations API Module
 * Handles batch product processing, vendor management, and traceability
 */

import { type RequestFunction } from './types'
import type {
  BatchProductProcessing,
  BatchProductProcessingCreateInput,
  BatchProduct,
  AssignInputsInput,
  IngestOutputsInput,
  ProcessingDashboard,
  AvailableProductsResponse,
  TraceabilityResponse,
  PaginatedBatchProductProcessings,
  FilterParams,
  ProcessingStatus,
  OilType,
} from './types'

export class ProcessingApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // PROCESSING MANAGEMENT
  // ============================================================================

  async getProcessings(params?: FilterParams & {
    status?: ProcessingStatus;
    vendor?: number;
  }): Promise<PaginatedBatchProductProcessings> {
    return this.request('/api/processing/processings/', { params })
  }

  async getProcessing(id: number): Promise<BatchProductProcessing> {
    return this.request(`/api/processing/processings/${id}/`)
  }

  async createProcessing(data: BatchProductProcessingCreateInput): Promise<BatchProductProcessing> {
    return this.request('/api/processing/processings/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateProcessing(id: number, data: Partial<BatchProductProcessingCreateInput & { status: ProcessingStatus }>): Promise<BatchProductProcessing> {
    return this.request(`/api/processing/processings/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteProcessing(id: number): Promise<void> {
    return this.request(`/api/processing/processings/${id}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // PROCESSING ACTIONS
  // ============================================================================

  async assignInputs(processingId: number, data: AssignInputsInput): Promise<{
    assigned_count: number;
    processing: BatchProductProcessing;
  }> {
    return this.request(`/api/processing/processings/${processingId}/assign_inputs/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async unassignInputs(processingId: number, data: AssignInputsInput): Promise<{
    unassigned_count: number;
    processing: BatchProductProcessing;
  }> {
    return this.request(`/api/processing/processings/${processingId}/unassign_inputs/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async markSent(processingId: number): Promise<BatchProductProcessing> {
    return this.request(`/api/processing/processings/${processingId}/mark_sent/`, {
      method: 'POST'
    })
  }

  async ingestOutputs(processingId: number, data: IngestOutputsInput): Promise<{
    created_count: number;
    outputs: BatchProduct[];
    processing: BatchProductProcessing;
  }> {
    return this.request(`/api/processing/processings/${processingId}/ingest_outputs/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async completeProcessing(processingId: number): Promise<BatchProductProcessing> {
    return this.request(`/api/processing/processings/${processingId}/complete/`, {
      method: 'POST'
    })
  }

  // ============================================================================
  // AVAILABLE PRODUCTS
  // ============================================================================

  async getAvailableProducts(params?: FilterParams & {
    oil_type?: OilType;
    container_id?: number;
    search?: string;
  }): Promise<AvailableProductsResponse> {
    return this.request('/api/processing/available-products/', { params })
  }

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  async getDashboard(): Promise<ProcessingDashboard> {
    return this.request('/api/processing/dashboard/')
  }

  // ============================================================================
  // TRACEABILITY
  // ============================================================================

  async getTraceability(barcode: string): Promise<TraceabilityResponse> {
    return this.request(`/api/processing/traceability/${barcode}/`)
  }
}
